import { AlertCircle, Lock, Mail, Receipt } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { GoogleIcon } from '@/components/google-icon'
import { LoadingButton } from '@/components/loading-button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authClient } from '@/lib/auth-client'

export function SignInPage() {
	const location = useLocation()
	const from = (location.state as { from?: string } | null)?.from ?? '/app'

	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [emailPending, setEmailPending] = useState(false)
	const [googlePending, setGooglePending] = useState(false)

	async function handleEmailSignIn(e: React.FormEvent) {
		e.preventDefault()
		setError(null)
		setEmailPending(true)
		try {
			const result = await authClient.signIn.email({
				email: email.trim().toLowerCase(),
				password,
			})
			if (result.error) {
				setError(result.error.message ?? 'Sign-in failed')
				return
			}
			window.location.replace(from)
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Sign-in failed')
		} finally {
			setEmailPending(false)
		}
	}

	async function handleGoogleSignIn() {
		setError(null)
		setGooglePending(true)
		try {
			await authClient.signIn.social({
				provider: 'google',
				callbackURL: `${window.location.origin}${from}`,
			})
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Could not start Google sign-in',
			)
			setGooglePending(false)
		}
	}

	const anyPending = emailPending || googlePending

	return (
		<section className="mx-auto flex min-h-[80vh] w-full max-w-md flex-col items-center justify-center px-4 py-10">
			<div className="mb-6 flex flex-col items-center gap-3 text-center">
				<div className="flex size-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
					<Receipt className="size-5" />
				</div>
				<div className="space-y-1">
					<h1 className="text-2xl font-semibold tracking-tight">
						Welcome back
					</h1>
					<p className="text-sm text-muted-foreground">
						Sign in to manage your receipts and warranties.
					</p>
				</div>
			</div>

			<Card className="w-full">
				<CardContent className="space-y-4 py-6">
					{error ? (
						<Alert variant="destructive">
							<AlertCircle />
							<AlertTitle>Couldn't sign in</AlertTitle>
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					) : null}

					<LoadingButton
						type="button"
						variant="outline"
						className="w-full"
						onClick={handleGoogleSignIn}
						pending={googlePending}
						disabled={anyPending}
					>
						<GoogleIcon className="size-4" />
						Continue with Google
					</LoadingButton>

					<div className="relative my-1 text-center text-xs text-muted-foreground">
						<span className="relative z-10 bg-card px-2 uppercase tracking-wide">
							or continue with email
						</span>
						<div className="absolute inset-x-0 top-1/2 -z-0 h-px bg-border" />
					</div>

					<form onSubmit={handleEmailSignIn} className="space-y-3" noValidate>
						<div className="space-y-1.5">
							<Label htmlFor="email">Email</Label>
							<div className="relative">
								<Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="email"
									type="email"
									autoComplete="email"
									className="pl-9"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									required
									disabled={anyPending}
								/>
							</div>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									id="password"
									type="password"
									autoComplete="current-password"
									className="pl-9"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									required
									disabled={anyPending}
								/>
							</div>
						</div>
						<LoadingButton
							type="submit"
							className="w-full"
							pending={emailPending}
							disabled={anyPending}
						>
							Sign in
						</LoadingButton>
					</form>

					<p className="text-center text-sm text-muted-foreground">
						No account?{' '}
						<Link
							to="/register"
							className="font-medium text-foreground hover:underline"
						>
							Create one
						</Link>
					</p>
				</CardContent>
			</Card>
		</section>
	)
}
